import {
  Paper,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import Autosuggest from 'react-autosuggest';
import { useDispatch, useSelector } from 'react-redux';

import {
  getRenderExpertSuggestionFunction,
  getSuggestionValue,
  renderInputComponent,
} from 'FRONTEND/components/Calendar/AppointmentForm/stepperHelper';
import StepTemplate from 'FRONTEND/components/Calendar/AppointmentForm/StepTemplate';
import Loader from 'FRONTEND/components/Loader';
import { pickExpert } from 'FRONTEND/reducers/bookingSlice';
import {
  clearSearch as clearExpertSearch,
  searchRequest as expertSearchRequest,
  selectManagedExpertById,
} from 'FRONTEND/reducers/expertSlice';
import { selectPatientById } from 'FRONTEND/reducers/patientSlice';
import { selectAllMatchingExperts } from 'FRONTEND/selectors/expertSelectors';
import { EXPERT_MINIMUM_SEARCH_CHARACTERS, MAX_SEARCH_RESULT_NUMBER } from 'FRONTEND/utils/constants';
import i18n from 'FRONTEND/utils/i18n';

export const ExpertStep = ({
  isCompleted,
  isLocked = false,
  onStepClick = () => {},
  shouldReset = false,
  onStepCompleted = () => {},
  ...otherProps
}) => {
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.app.requestCounter[expertSearchRequest.toString()] > 0);
  const selectedExpert = useSelector((state) => selectManagedExpertById(state, state.booking.selectedExpertId));
  const matchingExpertList = useSelector(selectAllMatchingExperts);
  const [expertQuery, setExpertQuery] = useState('');
  const selectedPatient = useSelector((state) => selectPatientById(state, state.booking.selectedPatientId));

  useEffect(() => {
    if (shouldReset) {
      setExpertQuery('');
      dispatch(pickExpert({ id: null }));
    }
  }, [shouldReset]);

  const handleExpertChange = (id) => {
    dispatch(pickExpert({ id }));
    onStepCompleted();
  };

  const selectedType = useSelector((state) => state.booking.selectedType);
  const expertSuggestProps = {
    renderInputComponent,
    suggestions: matchingExpertList.slice(0, MAX_SEARCH_RESULT_NUMBER),
    onSuggestionsFetchRequested: ({ value }) => value.length >= EXPERT_MINIMUM_SEARCH_CHARACTERS && dispatch(expertSearchRequest({
      name: value,
      appointmentType: selectedType,
      isManaged: true,
      tenantId: selectedPatient?.tenantId,
    })),
    onSuggestionsClearRequested: () => dispatch(clearExpertSearch()),
    getSuggestionValue,
    renderSuggestion: getRenderExpertSuggestionFunction(handleExpertChange),
    onSuggestionSelected: (event, { suggestion, method }) => method === 'enter' ? handleExpertChange(suggestion.id) : null,
  };

  return (
    <StepTemplate
      label={i18n._('Select a practitioner')}
      isLocked={isLocked}
      onEditClick={() => onStepClick()}
      isStepCompleted={selectedExpert && isCompleted}
      editText={selectedExpert?.alias || ''}
      tooltipText={i18n._('You can select another practitioner in the list on the left panel')}
      {...otherProps}
    >
      <div className='contactInput'>
        <Autosuggest
          open
          {...expertSuggestProps}
          inputProps={{
            autoFocus: true,
            placeholder: i18n._('Enter the practitioner’s name (minimum 3 characters)'),
            value: expertQuery,
            onChange: (event, { newValue }) => setExpertQuery(newValue),
          }}
          renderSuggestionsContainer={({ children, containerProps }) => (
            matchingExpertList.length > 0 ? (
              <Paper {...containerProps} square>
                {children}
              </Paper>
            ) : isLoading ? (
              <Loader isLoading={isLoading} position='verticalFlow' />
            ) : expertQuery && (
              <Typography>{i18n._('No expert matches your search.')}</Typography>
            )
          )}
        />
      </div>
    </StepTemplate>
  );
};
